import { Button } from '@components/button';
import { ExternalLink } from '@components/link';
import { Typography } from '@components/typography';
import { useGetInpaintFormatsQuery } from '@generated/graphql';
import { Stack } from '@mui/material';
import { useState } from 'react';
import { CreateInpaint } from './inpaint/CreateInpaint';

type VolumeService = 'Inpaint';

interface ServicesModalProps {
  directory: string[];
  volumeId: string;
  refetchInpaints: VoidFunction;
  onClose: VoidFunction;
}

export const ServicesModal = ({
  directory,
  volumeId,
  refetchInpaints,
  onClose,
}: ServicesModalProps) => {
  const options: VolumeService[] = ['Inpaint'];
  const [selectedOption, setSelectedOption] = useState<VolumeService | null>(null);

  const goBack = () => setSelectedOption(null);

  if (selectedOption === 'Inpaint') {
    return (
      <CreateInpaint
        refetch={refetchInpaints}
        onClose={onClose}
        goBack={goBack}
        volumeId={volumeId}
        folderHistory={directory}
      />
    );
  }

  return (
    selectedOption == null && (
      <Stack gap={5}>
        <Typography variant="h2" mb={6}>
          Create
        </Typography>
        {options.map((option) => (
          <Button key={option} fullWidth onClick={() => setSelectedOption(option)}>
            {option}
          </Button>
        ))}
      </Stack>
    )
  );
};

export const ServicesModalHelpContent = () => {
  const { data: inpaintFormats, loading: inpaintFormatsLoading } = useGetInpaintFormatsQuery();

  if (inpaintFormatsLoading) {
    return <Typography variant="body2">Loading...</Typography>;
  }

  return (
    <Stack gap={1}>
      <Typography variant="body2">
        Inpaint is a service used to remove objects from images by filling in the gaps with
        surrounding content, ensuring a seamless and natural appearance.
      </Typography>
      <Typography variant="body2">
        <strong>Key Parameters:</strong>
        <ul>
          <li>
            <strong>Location:</strong> The directory where contains all images and
            annotations/masks.
          </li>
          <li>
            <strong>Input type:</strong> Specifies the type of input used for mask generation, such
            as:
            <ul>
              {(inpaintFormats?.getInpaintFormats || []).map((type) => (
                <li key={type.value}>
                  <strong>{type.name}:</strong> {type.info}
                </li>
              ))}
            </ul>
          </li>
          <li>
            <strong>Output:</strong> The directory where the processed (inpainted) images are saved.
          </li>
          <li>
            <strong>Output type:</strong> Determines the output format, such as{' '}
            &quot;SATRGB_BACKGROUND&quot;,&quot;JPG&quot; or &quot;PNG&quot;. Choosing{' '}
            &quot;SATRGB_BACKGROUND&quot; will also generate a blend file with the background.
          </li>
          <li>
            <strong>Dilation:</strong> Expands the mask region to improve the inpainting result by
            setting a dilation value.
          </li>
        </ul>
        For more details, check the{' '}
        <ExternalLink href="https://github.com/Sanster/IOPaint">IOPaint Repository</ExternalLink>.
      </Typography>
    </Stack>
  );
};
